import React, { Suspense, lazy } from "react";
import { motion } from "framer-motion";

// Router Hook
import { useRoutes } from "react-router-dom";

// Auth
import ProtectedRoute from "./Utils/ProtectedRoutes";
import SellProtectedRoute from "./Utils/SellProtectedRoutes";
import ContinentalFinds from "./Pages/Screens/continental-finds";
import ExploreCulture from "./Pages/Screens/explore-culture";
import SellerAuth from "./sellers-dashboard/auth";
import Onboarding from "./sellers-dashboard/auth/onboarding";
import SellerSharedLayout from "./sellers-dashboard/dashboard/layouts/shared-layouts";
import Dashboard from "./sellers-dashboard/dashboard/pages/dashboard";
import SellerProducts from "./sellers-dashboard/dashboard/pages/products";
import EditProductPage from "./sellers-dashboard/dashboard/pages/products/edit-product-page";
import SellerProductApprovals from "./sellers-dashboard/dashboard/pages/product-approvals";
import SellerOrdersPage from "./sellers-dashboard/dashboard/pages/orders";
import OrderDetailPage from "./sellers-dashboard/dashboard/pages/orders/order-detail-page";
import SellerCustomersPage from "./sellers-dashboard/dashboard/pages/customers";
import SellerPayoutsPage from "./sellers-dashboard/dashboard/pages/payouts";
import SellerSettingsPage from "./sellers-dashboard/dashboard/pages/settings";
import SellerMessagesPage from "./sellers-dashboard/dashboard/pages/messages";
import SellerCreateAccount from "./sellers-dashboard/auth/create-account";
import CreateSellerStore from "./sellers-dashboard/auth/create-seller-store";
import SelectSellerPlan from "./sellers-dashboard/auth/select-plan";
import CategoryListings from "./Pages/Screens/categories";

const Login = lazy(() => import("./Pages/Auth/Login"));
const Register = lazy(() => import("./Pages/Auth/Register"));
const Verify = lazy(() => import("./Pages/Auth/Verify"));
const CreatePin = lazy(() => import("./Pages/Auth/CreatePin"));
const ForgetPassword = lazy(() => import("./Pages/Auth/ForgetPassword"));
const RegisterMain = lazy(() => import("./Pages/Auth/RegisterMain"));

const Home = lazy(() => import("./Pages/Screens/Home"));
const Download = lazy(() => import("./Pages/Screens/Download"));
const ProductView = lazy(() => import("./Pages/Screens/ProductView"));
const CategoryView = lazy(() => import("./Pages/Screens/CategoryView"));
const ServiceView = lazy(() => import("./Pages/Screens/ServiceView"));
const LatestProduct = lazy(() => import("./Pages/Screens/LatestProduct"));
const SearchResults = lazy(() => import("./Pages/Screens/SearchResults"));
const Wishlist = lazy(() => import("./Pages/Screens/Wishlist"));
const NoPage = lazy(() => import("./Pages/Screens/NoPage"));
const SingleBlog = lazy(() => import("./Pages/Screens/SingleBlog"));

const Cart = lazy(() => import("./Components/Cart/Cart"));
const Checkout = lazy(() => import("./Components/CheckOut/Checkout"));
const ServiceCheckout = lazy(
  () => import("./Components/CheckOut/ServiceCheckout"),
);
const Overview = lazy(() => import("./Components/ProductOrigins/Overview"));
const MaAgent = lazy(() => import("./Components/Common/MaAgent"));
const NotificationMobile = lazy(
  () => import("./Components/Common/NotificationMobile"),
);

const Blogs = lazy(() => import("./FooterContent/Blog/Blogs"));
const About = lazy(() => import("./FooterContent/About"));
const Ads = lazy(() => import("./FooterContent/Ads"));
const Contact = lazy(() => import("./FooterContent/Contact"));
const Faq = lazy(() => import("./FooterContent/Faq"));
const MamapurseFooter = lazy(() => import("./FooterContent/Mamapurse"));
const Payment = lazy(() => import("./FooterContent/Payment"));
const PrivacyPolicy = lazy(() => import("./FooterContent/PrivacyPolicy"));
const ProductDoc = lazy(() => import("./FooterContent/ProductDoc"));
const ReturnPolicy = lazy(() => import("./FooterContent/ReturnPolicy"));
const TermsConditions = lazy(() => import("./FooterContent/TermsConditions"));
const Verification = lazy(() => import("./FooterContent/Verification"));

const Chat = lazy(() => import("./Chat/Chat"));
const ChatOutlet = lazy(() => import("./Chat/ChatOutlet"));
const ChatMobile = lazy(() => import("./Chat/ChatMobile"));

const Profile = lazy(() => import("./Account-Profile/Pages/Profile"));
const SharedLayouts = lazy(
  () => import("./Account-Profile/Layouts/SharedLayouts"),
);
const Notification = lazy(() => import("./Account-Profile/Pages/Notification"));
const Security = lazy(() => import("./Account-Profile/Pages/Security"));
const Address = lazy(() => import("./Account-Profile/Pages/Address"));
const Mamapurse = lazy(() => import("./Account-Profile/Pages/Mamapurse"));
const HelpCenter = lazy(() => import("./Account-Profile/Pages/HelpCenter"));
const MyOrders = lazy(() => import("./Account-Profile/Pages/MyOrders"));
const Referral = lazy(() => import("./Account-Profile/Pages/Referral"));
const OrderList = lazy(
  () => import("./Account-Profile/Pages/MyOrder-Component/OrderList"),
);
const OrderDetails = lazy(
  () => import("./Account-Profile/Pages/MyOrder-Component/OrderDetails"),
);
const ServiceOrderDetails = lazy(
  () => import("./Account-Profile/Pages/MyOrder-Component/ServiceOrderDetails"),
);
const MyAccount = lazy(() => import("./Account-Profile/Layouts/MyAccount"));
const SellOnMamaket = lazy(() => import("./SellOnMamaket/SellOnMamaket"));

const Fallback = () => <div />;

const slideInVariants = {
  initial: { x: "-100vw" },
  animate: { x: 0 },
  exit: { x: "-100vw" },
};

export function Routes() {
  return useRoutes([
    {
      path: "/",
      element: (
        <Suspense fallback={<Fallback />}>
          <Home />
        </Suspense>
      ),
    },

    {
      path: "/sell-on-mamaket",
      element: (
        <Suspense fallback={<Fallback />}>
          <Onboarding />
        </Suspense>
      ),
    },
    {
      path: "/seller/select-plan",
      element: (
        <Suspense fallback={<Fallback />}>
          <SelectSellerPlan />
        </Suspense>
      ),
    },
    {
      path: "/seller-signup",
      element: (
        <Suspense fallback={<Fallback />}>
          <SellerCreateAccount />
        </Suspense>
      ),
    },
    {
      path: "/seller/create-store",
      element: (
        <ProtectedRoute>
          <Suspense fallback={<Fallback />}>
            <CreateSellerStore />
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: "/checkout",
      element: (
        <Suspense fallback={<Fallback />}>
          <Checkout />
        </Suspense>
      ),
    },
    {
      path: "/service/checkout",
      element: (
        <Suspense fallback={<Fallback />}>
          <ServiceCheckout />
        </Suspense>
      ),
    },
    {
      path: "/product-origins/:name/:id",
      element: (
        <Suspense fallback={<Fallback />}>
          <Overview />
        </Suspense>
      ),
    },
    {
      path: "/become-a-seller",
      element: (
        <Suspense fallback={<Fallback />}>
          <Onboarding />
        </Suspense>
      ),
    },
    {
      path: "/explore-culture",
      element: (
        <Suspense fallback={<Fallback />}>
          <ExploreCulture />
        </Suspense>
      ),
    },
    {
      path: "/continental-find/:slug",
      element: (
        <Suspense fallback={<Fallback />}>
          <ContinentalFinds />
        </Suspense>
      ),
    },
    {
      path: "/product-view/:id",
      element: (
        <Suspense fallback={<Fallback />}>
          <ProductView />
        </Suspense>
      ),
    },
    {
      path: "/service-view/:id",
      element: (
        <Suspense fallback={<Fallback />}>
          <ServiceView />
        </Suspense>
      ),
    },
    {
      path: "/latest-products",
      element: (
        <Suspense fallback={<Fallback />}>
          <LatestProduct />
        </Suspense>
      ),
    },
    {
      path: "/search",
      element: (
        <Suspense fallback={<Fallback />}>
          <SearchResults />
        </Suspense>
      ),
    },
    {
      path: "/ma-agent",
      element: (
        <Suspense fallback={<Fallback />}>
          <MaAgent />
        </Suspense>
      ),
    },
    {
      path: "/category-view/:name/:id",
      element: (
        <Suspense fallback={<Fallback />}>
          <CategoryView />
        </Suspense>
      ),
    },
    {
      path: "/categories",
      element: (
        <Suspense fallback={<Fallback />}>
          <CategoryListings />
        </Suspense>
      ),
    },
    {
      path: "/wishlist",
      element: (
        <Suspense fallback={<Fallback />}>
          <Wishlist />
        </Suspense>
      ),
    },
    {
      path: "/cart",
      element: (
        <Suspense fallback={<Fallback />}>
          <Cart />
        </Suspense>
      ),
    },
    {
      path: "/blog",
      element: (
        <Suspense fallback={<Fallback />}>
          <Blogs />
        </Suspense>
      ),
    },
    {
      path: "/blog/:name",
      element: (
        <Suspense fallback={<Fallback />}>
          <SingleBlog />
        </Suspense>
      ),
    },
    {
      path: "/chat",
      element: (
        <Suspense fallback={<Fallback />}>
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        </Suspense>
      ),
      children: [
        {
          path: "/chat/:id",
          element: (
            <Suspense fallback={<Fallback />}>
              <ChatOutlet />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: "/chat-mobile/:id",
      element: (
        <Suspense fallback={<Fallback />}>
          <ChatMobile />
        </Suspense>
      ),
    },
    {
      path: "/login",
      element: (
        <Suspense fallback={<Fallback />}>
          <motion.div
            variants={slideInVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <Login />
          </motion.div>
        </Suspense>
      ),
    },
    {
      path: "/register",
      element: (
        <Suspense fallback={<Fallback />}>
          <motion.div
            variants={slideInVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <RegisterMain />
          </motion.div>
        </Suspense>
      ),
    },
    {
      path: "/register-form",
      element: (
        <Suspense fallback={<Fallback />}>
          <motion.div
            variants={slideInVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            {" "}
            <Register />
          </motion.div>
        </Suspense>
      ),
    },
    {
      path: "/verify",
      element: (
        <Suspense fallback={<Fallback />}>
          <motion.div
            variants={slideInVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <Verify />
          </motion.div>
        </Suspense>
      ),
    },
    {
      path: "/createpin",
      element: (
        <Suspense fallback={<Fallback />}>
          <motion.div
            variants={slideInVariants}
            // initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            {" "}
            <CreatePin />
          </motion.div>
        </Suspense>
      ),
    },
    {
      path: "/notifications",
      element: (
        <Suspense fallback={<Fallback />}>
          <NotificationMobile />
        </Suspense>
      ),
    },

    {
      path: "/about-us",
      element: (
        <Suspense fallback={<Fallback />}>
          <About />
        </Suspense>
      ),
    },
    {
      path: "/ads",
      element: (
        <Suspense fallback={<Fallback />}>
          <Ads />
        </Suspense>
      ),
    },
    {
      path: "/contact",
      element: (
        <Suspense fallback={<Fallback />}>
          <Contact />
        </Suspense>
      ),
    },
    {
      path: "/faq",
      element: (
        <Suspense fallback={<Fallback />}>
          <Faq />
        </Suspense>
      ),
    },
    {
      path: "*",
      element: (
        <Suspense fallback={<Fallback />}>
          <NoPage />
        </Suspense>
      ),
    },
    {
      path: "/mamapurse",
      element: (
        <Suspense fallback={<Fallback />}>
          <MamapurseFooter />
        </Suspense>
      ),
    },
    {
      path: "/footer-product",
      element: (
        <Suspense fallback={<Fallback />}>
          <ProductDoc />
        </Suspense>
      ),
    },
    {
      path: "/payment",
      element: (
        <Suspense fallback={<Fallback />}>
          <Payment />
        </Suspense>
      ),
    },
    {
      path: "/privacy-policy",
      element: (
        <Suspense fallback={<Fallback />}>
          <PrivacyPolicy />
        </Suspense>
      ),
    },
    {
      path: "/product-policy",
      element: (
        <Suspense fallback={<Fallback />}>
          <ProductDoc />
        </Suspense>
      ),
    },
    {
      path: "/return-policy",
      element: (
        <Suspense fallback={<Fallback />}>
          <ReturnPolicy />
        </Suspense>
      ),
    },
    {
      path: "/terms&conditions",
      element: (
        <Suspense fallback={<Fallback />}>
          <TermsConditions />
        </Suspense>
      ),
    },
    {
      path: "/verification",
      element: (
        <Suspense fallback={<Fallback />}>
          <Verification />
        </Suspense>
      ),
    },

    {
      path: "/my-account",
      element: (
        <Suspense fallback={<Fallback />}>
          <MyAccount />
        </Suspense>
      ),
    },
    {
      path: "/forget_password",
      element: (
        <Suspense fallback={<Fallback />}>
          <ForgetPassword />
        </Suspense>
      ),
    },
    {
      element: (
        <Suspense fallback={<Fallback />}>
          <ProtectedRoute>
            {" "}
            <SharedLayouts />
          </ProtectedRoute>
        </Suspense>
      ),
      children: [
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <Profile />
            </Suspense>
          ),
          path: "/account",
        },
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <Notification />
            </Suspense>
          ),
          path: "/account/notification",
        },
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <Security />
            </Suspense>
          ),
          path: "/account/security",
        },
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <Referral />
            </Suspense>
          ),
          path: "/account/referral",
        },
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <Address />
            </Suspense>
          ),
          path: "/account/address",
        },
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <Mamapurse />
            </Suspense>
          ),
          path: "/account/mamapurse",
        },
        {
          path: "/account/mamapurse/confirmation",
          element: (
            <Suspense fallback={<Fallback />}>
              <Mamapurse />
            </Suspense>
          ),
        },
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <HelpCenter />
            </Suspense>
          ),
          path: "/account/help-center",
        },
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <MyOrders />
            </Suspense>
          ),
          // path: "/account/orders",
          children: [
            {
              element: (
                <Suspense fallback={<Fallback />}>
                  <OrderList />
                </Suspense>
              ),
              path: "/account/orders",
            },
            {
              element: (
                <Suspense fallback={<Fallback />}>
                  <OrderDetails />
                </Suspense>
              ),
              path: "/account/orders/order-details/product/:id",
            },
            {
              element: (
                <Suspense fallback={<Fallback />}>
                  <ServiceOrderDetails />
                </Suspense>
              ),
              path: "/account/orders/order-details/service/:id",
            },
          ],
        },
      ],
    },
    {
      element: (
        <Suspense fallback={<Fallback />}>
          <ProtectedRoute>
            <SellerSharedLayout />
          </ProtectedRoute>
        </Suspense>
      ),
      children: [
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <Dashboard />
            </Suspense>
          ),
          path: "/seller/dashboard",
        },
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <SellerProducts />
            </Suspense>
          ),
          path: "/seller/my-products",
        },
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <EditProductPage />
            </Suspense>
          ),
          path: "/seller/product/edit/:id",
        },
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <SellerProductApprovals />
            </Suspense>
          ),
          path: "/seller/product-approvals",
        },
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <SellerOrdersPage />
            </Suspense>
          ),
          path: "/seller/orders",
        },
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <OrderDetailPage />
            </Suspense>
          ),
          path: "/seller/orders/:orderId",
        },
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <SellerMessagesPage />
            </Suspense>
          ),
          path: "/seller/messages",
        },
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <SellerCustomersPage />
            </Suspense>
          ),
          path: "/seller/customers",
        },
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <SellerPayoutsPage />
            </Suspense>
          ),
          path: "/seller/payouts",
        },
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <SellerSettingsPage />
            </Suspense>
          ),
          path: "/seller/settings",
        },
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <Address />
            </Suspense>
          ),
          path: "/account/address",
        },
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <Mamapurse />
            </Suspense>
          ),
          path: "/account/mamapurse",
        },
        {
          path: "/account/mamapurse/confirmation",
          element: (
            <Suspense fallback={<Fallback />}>
              <Mamapurse />
            </Suspense>
          ),
        },
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <HelpCenter />
            </Suspense>
          ),
          path: "/account/help-center",
        },
        {
          element: (
            <Suspense fallback={<Fallback />}>
              <MyOrders />
            </Suspense>
          ),
          // path: "/account/orders",
          children: [
            {
              element: (
                <Suspense fallback={<Fallback />}>
                  <OrderList />
                </Suspense>
              ),
              path: "/account/orders",
            },
            {
              element: (
                <Suspense fallback={<Fallback />}>
                  <OrderDetails />
                </Suspense>
              ),
              path: "/account/orders/order-details/product/:id",
            },
            {
              element: (
                <Suspense fallback={<Fallback />}>
                  <ServiceOrderDetails />
                </Suspense>
              ),
              path: "//account/orders/order-details/service/:id",
            },
          ],
        },
      ],
    },
  ]);
}
